import React, { useState } from "react";
import {
  Button,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { UserRound, Lock, Eye, EyeOff } from "lucide-react";
import partnerTheme, { P } from "./partner/partnerTheme";
import { Field } from "./partner/PartnerUI";
import { ArtikaMark } from "./partner/PartnerNavbar";

import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "./firebase";
import { requestPermissionAndRegister } from "./firebase-notifications";
import { API_BASE } from "./utils/apiClient";
import { saveProfile } from "./utils/userProfile";



const Login = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

 const handleLogin = async () => {
  if (!username || !password) {
    alert("❌ اكتب اسم المستخدم وكلمة المرور");
    return;
  }

  try {
 const res = await fetch(
  `${API_BASE}/auth/login`,
  {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      username,
      password,
    }),
  }
);

    const data = await res.json();

    if (!res.ok) {
      alert(`❌ ${data.error || "كلمة المرور أو المستخدم خطأ"}`);
      return;
    }

    // ✅ جلسة Firebase حقيقية بالمتصفح (بدل توكن ثابت بـlocalStorage) — الـSDK
    // يجدد الـID token تلقائياً بالخلفية طول ما التبويب مفتوح، فما تنتهي صلاحيته
    // بعد ساعة وسط الشغل (كان سبب فشل رفع الصور بواجهة المقارنة بعد فترة طويلة
    // من فتح الصفحة).
    try {
      await signInWithEmailAndPassword(auth, data.email, password);
    } catch (fbErr) {
      console.error("Firebase client sign-in error:", fbErr);
      alert("❌ حدث خطأ أثناء تأمين جلسة الدخول، حاول مرة أخرى");
      return;
    }

    localStorage.setItem("uid", data.uid);
    localStorage.setItem("authToken", data.token);
    // username = الهوية الثابتة للحساب + الاسم الظاهر/البوكسات/الواجهات الإضافية
    saveProfile(data);

    onLogin(data.username);

    requestPermissionAndRegister(data.username);

    navigate(data.route);
  } catch (err) {
    console.error("Login error:", err);
    alert("❌ حدث خطأ أثناء الاتصال بالسيرفر");
  }
};
  const submit = async () => {
    setBusy(true);
    try {
      await handleLogin();
    } finally {
      setBusy(false);
    }
  };
  const onKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  };

  return (
    <ThemeProvider theme={partnerTheme}>
      <Box dir="rtl" sx={{ minHeight: "100vh", display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.05fr 1fr" }, background: P.bg, fontFamily: P.font, "& *": { fontFamily: "inherit" } }}>
        {/* لوحة الهوية */}
        <Box sx={{ position: "relative", overflow: "hidden", background: P.ink, color: "#fff", display: "flex", flexDirection: "column", justifyContent: "space-between", p: { xs: 3, md: 6 }, minHeight: { xs: 200, md: "auto" } }}>
          <Box aria-hidden sx={{ position: "absolute", left: -80, bottom: -80, width: 420, height: 420, border: "56px solid rgba(255,255,255,.05)" }} />
          <Box aria-hidden sx={{ position: "absolute", left: 40, bottom: 40, width: 200, height: 200, border: "30px solid rgba(255,255,255,.06)" }} />
          <Box aria-hidden sx={{ position: "absolute", inset: 0, background: "radial-gradient(700px 360px at 100% 0%, rgba(176,141,87,.32), transparent 70%)" }} />

          <Box sx={{ position: "relative", display: "flex", alignItems: "center", gap: 1.5 }}>
            <ArtikaMark size={36} color="#fff" bg={P.ink} />
            <Typography sx={{ fontWeight: 800, fontSize: 26, letterSpacing: "-0.02em", direction: "ltr" }}>Artika</Typography>
          </Box>

          <Box sx={{ position: "relative", display: { xs: "none", md: "block" } }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: P.goldLine, mb: 1.5, direction: "ltr", textAlign: "right", letterSpacing: ".04em" }}>
              smart art. human touch
            </Typography>
            <Typography sx={{ fontSize: 40, fontWeight: 800, lineHeight: 1.3, maxWidth: 440 }}>
              نظام تتبع الطلبات
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,.62)", fontSize: 16, mt: 1.5, maxWidth: 400, lineHeight: 1.8 }}>
              من الطلب إلى التسليم، كل مراحل اللوحة بمكان واحد.
            </Typography>
          </Box>

          <Typography sx={{ position: "relative", display: { xs: "none", md: "block" }, fontSize: 12.5, color: "rgba(255,255,255,.4)", direction: "ltr", textAlign: "right" }}>
            © {new Date().getFullYear()} Artika
          </Typography>
        </Box>

        {/* الفورم */}
        <Box sx={{ display: "flex", alignItems: { xs: "flex-start", md: "center" }, justifyContent: "center", px: 2, py: { xs: 0, md: 6 } }}>
          <Box sx={{ width: "100%", maxWidth: 400, mt: { xs: -5, md: 0 }, position: "relative", background: { xs: "#fff", md: "transparent" }, borderRadius: { xs: "20px", md: 0 }, boxShadow: { xs: P.shadowLg, md: "none" }, p: { xs: 3, md: 0 } }}>
            <Typography sx={{ fontSize: { xs: 24, md: 30 }, fontWeight: 800 }}>تسجيل الدخول</Typography>
            <Typography sx={{ color: P.muted, mt: 0.75, mb: 3.5, fontSize: 15 }}>أهلاً بك، سجّل دخولك حتى تكمل.</Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Field
                label="اسم المستخدم"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyDown={onKey}
                inputProps={{ style: { direction: "ltr", textAlign: "right" } }}
                InputProps={{ startAdornment: <InputAdornment position="start"><UserRound size={18} color={P.faint} /></InputAdornment> }}
              />
              <Field
                label="كلمة المرور"
                type={showPass ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={onKey}
                inputProps={{ style: { direction: "ltr", textAlign: "right" } }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Lock size={18} color={P.faint} /></InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPass((v) => !v)} aria-label={showPass ? "إخفاء كلمة المرور" : "إظهار كلمة المرور"}>
                        {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Button fullWidth variant="contained" onClick={submit} disabled={busy} sx={{ mt: 3, minHeight: 52, fontSize: 16 }}>
              {busy ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "دخول"}
            </Button>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default Login;
